import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getFieldValue, useSiteMasterRouteFields } from 'components/SiteMaster/utils';
import { getStateFromZipcodeCoveo } from 'lib/utils/coveoHeadless';
import {
  geocodeLocation,
  isValidLocationQuery,
} from 'lib/utils/coveo/sitesHeadless/geocodeLocation';
import { RootState } from 'src/store/store';

export type NearBySiteSearchLocation = {
  latitude?: number;
  longitude?: number;
  isLocationReady: boolean;
};

export type ProfileBasicDetails = {
  latitude?: number | string;
  longitude?: number | string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
};

type SiteSettingsAddressTypeItem = {
  fields?: {
    name?: {
      value?: string;
    };
  };
};

const getConfiguredAddressTypes = (types?: SiteSettingsAddressTypeItem[]): string[] =>
  types
    ?.map((item) => item?.fields?.name?.value?.trim())
    .filter((value): value is string => Boolean(value)) ?? [];

const getGeocodeOptionsFromSiteSettings = (siteSettings?: {
  addressType?: SiteSettingsAddressTypeItem[];
  cityType?: SiteSettingsAddressTypeItem[];
  stateType?: SiteSettingsAddressTypeItem[];
  zipCodeType?: { value?: string };
  countryCode?: { value?: string };
}) => {
  const cityTypes = getConfiguredAddressTypes(siteSettings?.cityType);
  const stateTypes = getConfiguredAddressTypes(siteSettings?.stateType);
  const zipCodeType = siteSettings?.zipCodeType?.value?.trim() || undefined;
  const acceptedTypes = Array.from(
    new Set([
      ...getConfiguredAddressTypes(siteSettings?.addressType),
      ...cityTypes,
      ...stateTypes,
      ...(zipCodeType ? [zipCodeType] : []),
    ])
  );
  const countryCode = siteSettings?.countryCode?.value?.trim() || undefined;

  return {
    ...(acceptedTypes.length > 0 ? { acceptedTypes } : {}),
    ...(cityTypes.length > 0 ? { cityTypes } : {}),
    ...(stateTypes.length > 0 ? { stateTypes } : {}),
    ...(zipCodeType ? { zipCodeType } : {}),
    ...(countryCode ? { countryCode } : {}),
  };
};

const isFiniteCoordinate = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

export const isTruthySitecoreParam = (value?: string | boolean): boolean =>
  value === true || value === '1' || value === 'true';

const parseCoordinateInput = (value?: string | number): number | null => {
  if (value === undefined || value === null) {
    return null;
  }

  const raw = typeof value === 'string' ? value.trim() : value;
  if (raw === '') {
    return null;
  }

  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseCoordinates = (
  latitudeValue?: string | number,
  longitudeValue?: string | number
): Pick<NearBySiteSearchLocation, 'latitude' | 'longitude'> => {
  const latitude = parseCoordinateInput(latitudeValue);
  const longitude = parseCoordinateInput(longitudeValue);

  if (latitude == null || longitude == null) {
    return {};
  }

  if (latitude === 0 && longitude === 0) {
    return {};
  }

  return { latitude, longitude };
};

const parseRouteCoordinates = (
  latitudeValue: string,
  longitudeValue: string
): Pick<NearBySiteSearchLocation, 'latitude' | 'longitude'> =>
  parseCoordinates(latitudeValue, longitudeValue);

export const parseProfileCoordinates = (
  latitudeValue?: number | string,
  longitudeValue?: number | string
): Pick<NearBySiteSearchLocation, 'latitude' | 'longitude'> =>
  parseCoordinates(latitudeValue, longitudeValue);

export const buildProfileAddress = (details?: ProfileBasicDetails): string => {
  if (!details) {
    return '';
  }

  const address = details.address?.trim();
  const city = details.city?.trim();
  const state = details.state?.trim();
  const zipCode = details.zipCode?.trim();

  const cityStateZip = [city, state].filter(Boolean).join(', ');
  const locality = [cityStateZip, zipCode].filter(Boolean).join(' ');

  return [address, locality].filter(Boolean).join(', ');
};

export function useNearBySiteSearchLocation(useProfileLocation: boolean): NearBySiteSearchLocation {
  const routeFields = useSiteMasterRouteFields();
  const { sitecoreContext } = useSitecoreContext();
  const siteSettings: any = sitecoreContext?.siteSettings;
  const basicDetails = useSelector(
    (state: RootState) => state.crProfile?.profileData?.basicDetails
  ) as ProfileBasicDetails | undefined;
  const [geocodedCoords, setGeocodedCoords] = useState<Pick<
    NearBySiteSearchLocation,
    'latitude' | 'longitude'
  > | null>(null);
  const [isGeocoding, setIsGeocoding] = useState(false);

  const siteCoords = useMemo(
    () =>
      parseRouteCoordinates(
        getFieldValue(routeFields.siteLatitude),
        getFieldValue(routeFields.siteLongitude)
      ),
    [routeFields.siteLatitude, routeFields.siteLongitude]
  );

  const profileCoords = useMemo(
    //using basicDetails to get the lat/long coordinates
    () => parseProfileCoordinates(basicDetails?.latitude, basicDetails?.longitude),
    [basicDetails?.latitude, basicDetails?.longitude]
  );

  const hasProfileCoords = Boolean(
    profileCoords.latitude != null && profileCoords.longitude != null
  );

  const profileAddress = useMemo(() => buildProfileAddress(basicDetails), [basicDetails]);

  useEffect(() => {
    if (!useProfileLocation || hasProfileCoords) {
      setGeocodedCoords(null);
      setIsGeocoding(false);
      return;
    }

    let cancelled = false;
    setIsGeocoding(true);
    setGeocodedCoords(null);

    const resolveProfileCoordinates = async () => {
      let mapApiKey: string | undefined;
      try {
        mapApiKey = siteSettings?.googleMapsAPIKey?.value
          ? atob(siteSettings.googleMapsAPIKey.value)
          : undefined;
      } catch {
        mapApiKey = undefined;
      }

      try {
        if (profileAddress && isValidLocationQuery(profileAddress)) {
          const addressResult = await geocodeLocation(
            profileAddress,
            mapApiKey,
            getGeocodeOptionsFromSiteSettings(siteSettings)
          );

          if (cancelled) {
            return;
          }

          if (
            isFiniteCoordinate(addressResult?.latitude) &&
            isFiniteCoordinate(addressResult?.longitude)
          ) {
            //setting the geocoded coords to the address result if lat/long is not present in basicDetails
            setGeocodedCoords({
              latitude: addressResult.latitude,
              longitude: addressResult.longitude,
            });
            return;
          }
        }

        //using zipCode to get the lat/long coordinates only when lat/long is not present in basicDetails or the address is not valid
        const zipCode = basicDetails?.zipCode?.trim();
        if (zipCode && isValidLocationQuery(zipCode)) {
          const zipResult = await getStateFromZipcodeCoveo(zipCode, mapApiKey);

          if (cancelled) {
            return;
          }

          if (isFiniteCoordinate(zipResult?.latitude) && isFiniteCoordinate(zipResult?.longitude)) {
            setGeocodedCoords({
              latitude: zipResult.latitude,
              longitude: zipResult.longitude,
            });
            return;
          }
        }

        if (!cancelled) {
          setGeocodedCoords({});
        }
      } catch {
        if (!cancelled) {
          setGeocodedCoords({});
        }
      } finally {
        if (!cancelled) {
          setIsGeocoding(false);
        }
      }
    };

    void resolveProfileCoordinates();

    return () => {
      cancelled = true;
    };
  }, [
    useProfileLocation,
    hasProfileCoords,
    profileAddress,
    basicDetails?.zipCode,
    siteSettings?.googleMapsAPIKey?.value,
    siteSettings?.addressType,
    siteSettings?.cityType,
    siteSettings?.stateType,
    siteSettings?.zipCodeType?.value,
    siteSettings?.countryCode?.value,
  ]);

  if (!useProfileLocation) {
    return {
      ...siteCoords,
      isLocationReady: true,
    };
  }

  if (hasProfileCoords) {
    return {
      ...profileCoords,
      isLocationReady: true,
    };
  }

  if (isGeocoding || geocodedCoords === null) {
    return {
      isLocationReady: false,
    };
  }

  return {
    ...geocodedCoords,
    isLocationReady: true,
  };
}
